import type { AnalysisResult, RateLimitInfo } from '../types';

interface ScrapeResponse {
  csrHtml: string;
  ssrHtml: string | null;
  sourceUrl: string;
  sourceTitle?: string;
}

export class RateLimitError extends Error {
  info: RateLimitInfo;
  constructor(info: RateLimitInfo) {
    super(info.message);
    this.name = 'RateLimitError';
    this.info = info;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* fall through */
  }

  if (res.status === 429 || res.status === 503) {
    const rawScope = res.headers.get('X-RateLimit-Scope') || 'burst';
    const scope: RateLimitInfo['scope'] =
      rawScope === 'daily' || rawScope === 'global' || rawScope === 'blocked'
        ? rawScope
        : 'burst';
    const resetAt = parseInt(res.headers.get('X-RateLimit-Reset') || '0', 10) || 0;
    throw new RateLimitError({
      scope,
      message: data?.error || 'Rate limit exceeded',
      resetAt,
    });
  }

  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

export function scrapeUrl(url: string): Promise<ScrapeResponse> {
  return postJson<ScrapeResponse>('/api/scrape', { url });
}

export function analyzeHtml(
  html: string,
  meta?: { sourceUrl?: string; sourceTitle?: string; ssrHtml?: string | null }
): Promise<AnalysisResult> {
  return postJson<AnalysisResult>('/api/analyze', {
    html,
    sourceUrl: meta?.sourceUrl,
    sourceTitle: meta?.sourceTitle,
    ssrHtml: meta?.ssrHtml ?? undefined,
  });
}
